import { Link, type LinkProps } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface RouteTab {
  to: LinkProps["to"];
  label: ReactNode;
  icon?: ReactNode;
  count?: number;
}

/**
 * Tabs that are routes (URL changes, back button works). Current params and search (e.g. ?env=) are kept.
 *
 *   <RouteTabs items={[{ to: "/$workspace/$project/settings/variables", label: "Variables" }, …]} />
 */
export function RouteTabs({ items, className }: { items: RouteTab[]; className?: string }) {
  return (
    <nav className={cn("scrollbar-thin -mx-1 flex items-center gap-5 overflow-x-auto border-b border-border px-1", className)} aria-label="Sections">
      {items.map((t) => (
        <Link
          key={String(t.to)}
          to={t.to}
          params={((p: Record<string, string>) => p) as never}
          search={((s: Record<string, unknown>) => s) as never}
          className={cn(
            "-mb-px inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent pb-2.5 pt-1 text-base font-medium text-muted transition-colors hover:text-text [&_svg]:size-4",
            "data-[status=active]:border-accent data-[status=active]:text-text",
          )}
        >
          {t.icon}
          {t.label}
          {t.count != null && t.count > 0 && <span className="rounded-full bg-surface-3 px-1.5 text-2xs font-semibold text-muted">{t.count}</span>}
        </Link>
      ))}
    </nav>
  );
}
